import { createSelector } from 'reselect';
import {
  currentClientIdSelector,
  isConsumerArea as isConsumerAreaSelector,
  hasAccessToManagedAccountsSelector,
  showMgpIntegrationSelector,
  showEMoneyIntegrationSelector,
  showSchwabIntegrationSelector,
  showOrionTradingSelector,
  hasDataReconciliationSelector,
  showPortfolioReportsSelector,
  isFinancialPlanEnabledSelector,
  currentClientSelector,
  showRISAIntegrationSelector,
  selectHasRightCapitalAccess,
} from '../../CommonSelectors/CommonAreaSelector';
import { currentRouteSelector } from '../../CommonComponents/Router/Selector';
import {
  clientSelector,
  showReportsSelector as consumerShowReportsSelector,
  selectIsProfileEnable,
} from '../../ConsumerArea/ConsumerAreaSelector';
import { isDocumentVaultVisibleSelector } from '../../AdviserArea/AdviserAreaSelector';
import { getRouteDefaultParamsMap } from '../../CommonComponents/Router/utils/getRouteDefaultParamsMap';
import { beFiPlanSubFeatures } from '../../ClientAccess/components/ClientAccessSettings/sections/Dashboard/Dashboard.meta';

export const currentlySelectedNavigationItemSelector = createSelector(
  currentRouteSelector,
  ({ controller }) => {
    switch (controller) {
      case 'ProfileAccounts':
      case 'ProfileStockOptions':
      case 'ProfileInsurance':
      case 'ProfileExpenses':
      case 'ProfileIncome':
      case 'ProfileRiskTolerance':
      case 'Profile':
        return 'Profile';
      case 'ProjectedCashFlowReport':
      case 'ProjectedBalanceSheet':
      case 'AdvancedReports':
      case 'TasksV2':
      case 'ReactAssetAllocationReport':
      case 'PortfolioReports':
        return 'Reports';
      case 'GoalsSummary':
        return 'Flag';
      case 'ConsumerOverview':
        return 'Heart';
      case 'Transactions':
      case 'NetWorth':
      case 'AccountPerformanceReporting':
      case 'ApexAccount':
        return 'Finances';
      case 'DocumentVault':
        return 'Vault';
      case 'ManagedAccounts':
        return 'Apex';
      case 'MoneyGuidePro':
        return 'MoneyGuidePro';
      case 'EMoney':
        return 'eMoney';
      case 'Schwab':
        return 'Schwab';
      case 'RightCapital':
        return 'RightCapital';
      case 'RISA':
        return 'RISA';
      case 'OrionTrading':
        return 'OrionTrading';
      case 'DataReconciliation':
        return 'DataReconciliation';
      case 'Newsfeed':
        return 'Newsfeed';
      case 'BeFi':
        return 'BeFi';
      default:
        return '';
    }
  },
);

export const isFinancialPlanSelectedSelector = createSelector(
  currentlySelectedNavigationItemSelector,
  selected => selected === 'Flag',
);

export const allNavigationItemsSelector = createSelector(
  currentClientIdSelector,
  currentlySelectedNavigationItemSelector,
  (clientId, selected) => {
    const routeParamsMap = getRouteDefaultParamsMap(clientId);

    return [
      {
        id: 'ConsumerOverview',
        icon: 'fa fa-tachometer-alt',
        component: 'ConsumerOverview',
        parameters: routeParamsMap.ConsumerOverview,
        isSelected: selected === 'Heart',
        module: 'Overview',
        tooltip: 'Client Overview',
      },
      {
        id: 'BeFi',
        icon: 'custom',
        component: 'BeFi',
        parameters: routeParamsMap.BeFi,
        isSelected: selected === 'BeFi',
        module: 'BeFi',
        tooltip: 'BeFi',
      },
      {
        id: 'Newsfeed',
        icon: 'far fa-newspaper',
        component: 'Newsfeed',
        parameters: routeParamsMap.Newsfeed,
        isSelected: selected === 'Newsfeed',
        module: 'Newsfeed',
        tooltip: 'Newsfeed',
      },
      {
        id: 'PersonalFinances',
        icon: 'fa fa-university',
        component: 'Transactions',
        parameters: routeParamsMap.Transactions,
        isSelected: selected === 'Finances',
        module: 'CurrentFinances',
        tooltip: 'Personal Finances',
      },
      {
        id: 'Profile',
        icon: 'fa fa-user-circle',
        component: 'Profile',
        parameters: routeParamsMap.Profile,
        isSelected: selected === 'Profile',
        module: 'Profile',
        tooltip: 'Profile',
      },
      {
        id: 'GoalTrack',
        icon: 'fas fa-chart-bar',
        component: 'GoalTrack',
        parameters: routeParamsMap.GoalTrack,
        isSelected: selected === 'GoalTrack',
        module: 'GoalTrack',
        tooltip: 'Goal Track',
      },
      {
        id: 'Schwab',
        icon: 'custom',
        component: 'Schwab',
        parameters: routeParamsMap.Schwab,
        isSelected: selected === 'Schwab',
        module: 'Schwab',
        tooltip: 'Schwab Alliance',
      },
      {
        id: 'MoneyGuidePro',
        icon: 'custom',
        component: 'MoneyGuidePro',
        parameters: routeParamsMap.MoneyGuidePro,
        isSelected: selected === 'MoneyGuidePro',
        module: 'MoneyGuidePro',
        tooltip: 'Financial Planning',
      },
      {
        id: 'eMoney',
        icon: 'custom',
        component: 'EMoney',
        parameters: routeParamsMap.EMoney,
        isSelected: selected === 'eMoney',
        module: 'eMoney',
        tooltip: 'Financial Planning',
      },
      {
        id: 'RightCapital',
        icon: 'custom',
        component: 'RightCapital',
        parameters: routeParamsMap.RightCapital,
        isSelected: selected === 'RightCapital',
        module: 'RightCapital',
        tooltip: 'Financial Planning',
      },
      {
        id: 'OrionTrading',
        icon: 'far fa-random',
        component: 'OrionTrading',
        parameters: routeParamsMap.OrionTrading,
        isSelected: selected === 'OrionTrading',
        module: 'OrionTrading',
        tooltip: 'Trading',
      },
      {
        id: 'Reports',
        icon: 'far fa-file-alt',
        component: 'ProjectedCashFlowReport',
        parameters: routeParamsMap.ProjectedCashFlowReport,
        isSelected: selected === 'Reports',
        module: 'FinancialPlan',
        tooltip: 'Reports',
      },
      {
        id: 'DocumentVault',
        icon: 'fa fa-folder',
        component: 'DocumentVault',
        parameters: routeParamsMap.DocumentVault,
        isSelected: selected === 'Vault',
        module: 'Document Vault',
        tooltip: 'Document Vault',
      },
      {
        id: 'DataReconciliation',
        icon: 'fas fa-tasks',
        component: 'DataReconciliation',
        parameters: routeParamsMap.DataReconciliation,
        isSelected: selected === 'DataReconciliation',
        module: 'DataReconciliation',
        tooltip: 'Reconciled Held-Away Accounts',
      },
      {
        id: 'RISA',
        icon: 'custom',
        component: 'RISA',
        parameters: routeParamsMap.RISA,
        isSelected: selected === 'RISA',
        module: 'RISA',
        tooltip: 'Retirement Income Style Awareness',
      },
    ];
  },
);

export const consumerAreaNavigationItemsSelector = createSelector(
  allNavigationItemsSelector,
  clientSelector,
  consumerShowReportsSelector,
  hasAccessToManagedAccountsSelector,
  showMgpIntegrationSelector,
  showEMoneyIntegrationSelector,
  showSchwabIntegrationSelector,
  selectHasRightCapitalAccess,
  showRISAIntegrationSelector,
  showOrionTradingSelector,
  hasDataReconciliationSelector,
  showPortfolioReportsSelector,
  isFinancialPlanEnabledSelector,
  currentClientSelector,
  selectIsProfileEnable,
  (
    items,
    { ModulesToHide, IsOrionClient, ShowGoalTracking },
    showReports,
    showManagedAccounts,
    showMgpIntegration,
    showEMoneyIntegration,
    showSchwabIntegration,
    hasRightCapitalAccess,
    showRISAIntegration,
    showOrionTrading,
    hasDataReconciliation,
    showPortfolioReports,
    isFinancialPlanEnabled,
    { ShowNewsFeed, DisplayBehavioralFinanceSection, OptOuts },
    isProfileEnabled,
  ) => {
    return items.filter(({ module }) => {
      if (module === 'FinancialPlan') {
        if (isFinancialPlanEnabled) {
          return showReports;
        }
        return showPortfolioReports;
      }

      if (module === 'Profile') {
        return !isProfileEnabled ? isProfileEnabled : isFinancialPlanEnabled;
      }

      if (IsOrionClient && module === 'Overview') {
        return isFinancialPlanEnabled;
      }

      if (module === 'Newsfeed') {
        return !ModulesToHide.includes('Newsfeed') && ShowNewsFeed;
      }

      if (module === 'GoalTrack') {
        return ShowGoalTracking;
      }

      if (module === 'BeFi') {
        return (
          DisplayBehavioralFinanceSection &&
          (!OptOuts.includes('AssetBucketing') ||
            !OptOuts.includes('Rtq') ||
            !OptOuts.includes('BeFi20') ||
            !OptOuts.includes('PulseCheck'))
        );
      }

      return (
        ModulesToHide.findIndex(mod => mod === module) === -1 &&
        (module !== 'ManagedAccounts' || showManagedAccounts) &&
        (module !== 'MoneyGuidePro' || showMgpIntegration) &&
        (module !== 'eMoney' || showEMoneyIntegration) &&
        (module !== 'Schwab' || showSchwabIntegration) &&
        (module !== 'RightCapital' || hasRightCapitalAccess) &&
        (module !== 'OrionTrading' || showOrionTrading) &&
        (module !== 'DataReconciliation' || hasDataReconciliation) &&
        (module !== 'RISA' || showRISAIntegration)
      );
    });
  },
);

export const adviserAreaNavigationItemsSelector = createSelector(
  allNavigationItemsSelector,
  hasAccessToManagedAccountsSelector,
  isDocumentVaultVisibleSelector,
  showMgpIntegrationSelector,
  showEMoneyIntegrationSelector,
  showSchwabIntegrationSelector,
  selectHasRightCapitalAccess,
  showRISAIntegrationSelector,
  showOrionTradingSelector,
  hasDataReconciliationSelector,
  isFinancialPlanEnabledSelector,
  showPortfolioReportsSelector,
  currentClientSelector,
  (
    items,
    showManagedAccounts,
    isDocumentVaultVisible,
    showMgpIntegration,
    showEMoneyIntegration,
    showSchwabIntegration,
    hasRightCapitalAccess,
    showRISAIntegration,
    showOrionTrading,
    showDataReconciliation,
    isFinancialPlanEnabled,
    showPortfolioReport,
    { ShowNewsFeed, ShowGoalTracking, ClientAccess },
  ) => {
    return items.filter(({ module }) => {
      switch (module) {
        case 'ManagedAccounts':
          return showManagedAccounts;
        case 'Document Vault':
          return isDocumentVaultVisible;
        case 'MoneyGuidePro':
          return showMgpIntegration;
        case 'eMoney':
          return showEMoneyIntegration;
        case 'Schwab':
          return showSchwabIntegration;
        case 'RISA':
          return showRISAIntegration;
        case 'OrionTrading':
          return showOrionTrading;
        case 'DataReconciliation':
          return showDataReconciliation;
        case 'Profile':
          return isFinancialPlanEnabled;
        case 'FinancialPlan':
          return !isFinancialPlanEnabled ? showPortfolioReport : true;
        case 'Newsfeed':
          return ShowNewsFeed;
        case 'RightCapital':
          return hasRightCapitalAccess;
        case 'GoalTrack':
          return ShowGoalTracking;
        case 'BeFi': {
          if (!ClientAccess) {
            return false;
          }

          const {
            DisplayBehavioralFinanceSection,
            DashboardAccessSet,
            HiddenBeFiTools,
          } = ClientAccess;

          return (
            DisplayBehavioralFinanceSection &&
            beFiPlanSubFeatures.some(
              val => DashboardAccessSet.includes(val) && !HiddenBeFiTools.includes(val),
            )
          );
        }

        default:
          return true;
      }
    });
  },
);

export const navigationItemsSelector = createSelector(
  isConsumerAreaSelector,
  state => state,
  (isConsumerArea, state) => {
    if (isConsumerArea) {
      return consumerAreaNavigationItemsSelector(state);
    }
    return adviserAreaNavigationItemsSelector(state);
  },
);

export const hasFinancialPlanSelector = createSelector(
  isConsumerAreaSelector,
  state => state,
  (isConsumerArea, state) => {
    if (isConsumerArea) {
      return (
        clientSelector(state).ModulesToHide.findIndex(mod => mod === 'FinancialPlan') === -1 &&
        isFinancialPlanEnabledSelector(state)
      );
    }
    return isFinancialPlanEnabledSelector(state);
  },
);
